import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT } from "@/components/SubmissionsTable";

const ROLES = ["driver", "runner", "admin", "ceo", "coceo", "dev"];

function CreateUser({ onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "driver", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Account created for ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "driver", phone: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="create-user-form">
      <h3 className="font-display text-xl font-bold tracking-tight">Create User Account</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Full Name</Label>
          <Input data-testid="new-user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
          <Input data-testid="new-user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
          <Input data-testid="new-user-password" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role" className="rounded-sm border-input bg-background focus:ring-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {ROLES.map((r) => <SelectItem key={r} value={r} className="uppercase">{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button data-testid="create-user-button" type="submit" disabled={busy}
        className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
        {busy ? "Creating..." : "Create Account"}
      </Button>
    </form>
  );
}

export default function DevPanel() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);

  const refresh = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/dev/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/dev/db-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Tabs defaultValue="users" data-testid="dev-panel">
      <TabsList className="mb-6 rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-dev-users" value="users" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Users</TabsTrigger>
        <TabsTrigger data-testid="tab-dev-logs" value="logs" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">System Logs</TabsTrigger>
        <TabsTrigger data-testid="tab-dev-db" value="db" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Database</TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="space-y-6">
        <CreateUser onDone={refresh} />
        <div className="rounded-sm border border-border" data-testid="users-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Name</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-border hover:bg-muted/50" data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="font-mono text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-sm font-bold uppercase tracking-wider ${u.role === "dev" ? "border-primary/50 text-primary" : "border-input text-muted-foreground"}`}>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDT(u.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="logs">
        <div className="rounded-sm border border-border" data-testid="logs-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Time</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Action</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Actor</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="border-border hover:bg-muted/50">
                  <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">{fmtDT(l.timestamp)}</TableCell>
                  <TableCell className="font-mono text-xs font-bold text-primary">{l.action}</TableCell>
                  <TableCell className="text-sm">{l.actor}</TableCell>
                  <TableCell className="max-w-[280px] truncate text-sm text-muted-foreground">{l.details}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="db">
        <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/40 md:grid-cols-4" data-testid="db-stats">
          {stats.map((s) => (
            <div key={s.collection} className="bg-card p-6">
              <p className="font-mono text-xs font-bold uppercase tracking-widest text-muted-foreground">{s.collection}</p>
              <p className="mt-2 font-display text-3xl font-black">{s.count}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">documents</p>
            </div>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
}
